import { useCallback, useState, useMemo, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { updateScooterStatusAsync, selectScooters, addScooterNoteAsync } from '../../redux/slices/scootersSlice';
import { styled } from '@mui/material/styles';
import {
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControl,
  Grid,
  IconButton,
  InputLabel,
  MenuItem,
  Menu,
  Paper,
  Select,
  Table,
  TableBody,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography,
  Pagination as MuiPagination,
  List,
  ListItem,
  ListItemText,
  Autocomplete,
  Tabs,
  Tab,
  Divider
} from '@mui/material';
import {
  Add,
  Pending,
  Build,
  Close,
  AddCircle,
  RemoveCircle,
  CheckCircle,
  LocationCity,
  Block,
  Menu as MenuIcon
} from '@mui/icons-material';
import Title from '../../components/Title';
import {
  useGetWorkshopTasksQuery,
  useGetWorkshopTaskStatsQuery,
  useCreateWorkshopTaskMutation,
  useUpdateWorkshopTaskMutation,
  useStartWorkshopTaskMutation,
  useFinishWorkshopTaskMutation,
  useDeleteWorkshopTaskMutation
} from '../../redux/api/workshopTasksApi';
import { useGetSparePartsQuery, useGetWarehousesQuery } from '../../redux/api/sparePartsApi';
import {
  WorkshopTask,
  WorkshopTaskStatus,
  CreateWorkshopTaskBody,
  FinishWorkshopTaskBody
} from '../../resources/types/workshopTasksTypes';
import { SparePart } from '../../resources/types/sparePartsTypes';
import { TableCell as MuiTableCell } from '@mui/material';
import moment from 'moment';
import axios from '../../resources/axios';
import { selectAdmins } from '../../redux/slices/adminsSlice';
import { ScooterStatus } from '../ZonesPage/interface';
import { RootState } from '../../redux/store';

const Root = styled('div')({
  padding: 0,
  minHeight: 'calc(100vh - 180px)'
});

const StatsSection = styled(Box)({
  marginBottom: 24
});

const FilterSection = styled(Box)({
  marginBottom: 24,
  display: 'flex',
  gap: 16,
  flexWrap: 'wrap',
  alignItems: 'center'
});

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  '&:hover': {
    backgroundColor:
      theme.palette.mode === 'light'
        ? 'rgba(0, 0, 0, 0.04)'
        : 'rgba(255, 255, 255, 0.08)'
  },
  transition: 'background-color 0.2s ease'
}));

const StyledTableCell = styled(MuiTableCell)(({ theme }) => ({
  borderBottom: `1px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.08)'
      : 'rgba(255, 255, 255, 0.1)'
  }`,
  padding: '12px 16px'
}));

const TableHeaderCell = styled(MuiTableCell)(({ theme }) => ({
  fontWeight: 700,
  backgroundColor: theme.palette.mode === 'light' ? '#F5F5F5' : '#2A2A2A',
  borderBottom: `2px solid ${
    theme.palette.mode === 'light'
      ? 'rgba(0, 0, 0, 0.12)'
      : 'rgba(255, 255, 255, 0.12)'
  }`,
  padding: '12px 16px'
}));

const PaginationWrapper = styled(Box)({
  display: 'flex',
  justifyContent: 'center',
  marginTop: 24
});

const hexToRgba = (hex: string, alpha: number = 1): string => {
  const r = parseInt(hex.slice(1, 3), 16);
  const g = parseInt(hex.slice(3, 5), 16);
  const b = parseInt(hex.slice(5, 7), 16);
  return `rgba(${r}, ${g}, ${b}, ${alpha})`;
};

const StatCard = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor' && prop !== 'isActive'
})<{ bgColor: string; isActive?: boolean }>(({ bgColor, isActive }) => ({
  display: 'flex',
  flexDirection: 'row',
  alignItems: 'center',
  justifyContent: 'flex-start',
  padding: '14px 16px',
  borderRadius: '12px',
  transition: 'all 0.2s ease',
  backgroundColor: isActive ? hexToRgba(bgColor, 0.25) : hexToRgba(bgColor, 0.12),
  boxShadow: isActive ? '0 4px 12px rgba(0, 0, 0, 0.15)' : '0 1px 4px rgba(0, 0, 0, 0.08)',
  minWidth: 160,
  minHeight: 80,
  position: 'relative',
  cursor: 'pointer',
  border: isActive ? `2px solid ${bgColor}` : 'none',
  
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 4px 12px rgba(0, 0, 0, 0.15)',
  }
}));

const IconCircle = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'bgColor'
})<{ bgColor: string }>(({ bgColor }) => ({
  width: 40,
  height: 40,
  borderRadius: '8px',
  backgroundColor: hexToRgba(bgColor, 0.18),
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  flexShrink: 0,
  '& svg': {
    fontSize: 24,
    color: bgColor
  }
}));

const CountText = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor'
})<{ textColor: string }>(({ textColor }) => ({
  fontSize: 32,
  fontWeight: 700,
  color: textColor,
  lineHeight: 1,
  position: 'absolute',
  right: '16px',
  top: '50%',
  transform: 'translateY(-50%)'
}));

const LabelText = styled(Typography)(({ theme }) => ({
  fontSize: 11,
  fontWeight: 600,
  color: theme.palette.mode === 'light' ? '#616161' : '#B0B0B0',
  textTransform: 'uppercase',
  letterSpacing: '0.8px',
  textAlign: 'left',
  marginLeft: 12
}));

const WorkshopTasksPage = () => {
  const dispatch = useDispatch();
  const adminInfo = useSelector(selectAdmins);
  const scooters = useSelector(selectScooters);
  const currentCityId = useMemo(() => adminInfo.currentCity, [adminInfo.currentCity]);

  const [page, setPage] = useState(1);
  const [statusFilter, setStatusFilter] = useState<WorkshopTaskStatus | ''>('');
  const [scooterNameFilter, setScooterNameFilter] = useState('');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [finishDialogOpen, setFinishDialogOpen] = useState(false);
  const [editingTask, setEditingTask] = useState<WorkshopTask | null>(null);
  const [formData, setFormData] = useState<Partial<CreateWorkshopTaskBody>>({
    scooterName: '',
    description: ''
  });
  const [finishData, setFinishData] = useState<FinishWorkshopTaskBody>({
    note: '',
    spareParts: [],
    photos: []
  });
  const [selectedSpareParts, setSelectedSpareParts] = useState<Array<{ id: string; qty: number; warehouseId: string }>>([]);
  const [uploadedPhotos, setUploadedPhotos] = useState<string[]>([]);
  const [uploading, setUploading] = useState(false);
  const [activeTab, setActiveTab] = useState(0);
  const [sparePartsSearch, setSparePartsSearch] = useState('');
  const [statusMenuAnchor, setStatusMenuAnchor] = useState<{
    element: HTMLElement | null;
    taskId: string;
  }>({ element: null, taskId: '' });
  const [scooterStatusMenuAnchor, setScooterStatusMenuAnchor] = useState<{
    element: HTMLElement | null;
    scooterId: string;
  }>({ element: null, scooterId: '' });
  const [scooterNotes, setScooterNotes] = useState<any[]>([]);
  const [loadingNotes, setLoadingNotes] = useState(false);

  const { data: tasksData, isLoading, error: tasksError } = useGetWorkshopTasksQuery({
    page,
    limit: 20,
    status: statusFilter || undefined,
    scooterName: scooterNameFilter || undefined
  });

  const { data: sparePartsData } = useGetSparePartsQuery({
    limit: 100
  });

  const { data: warehouses } = useGetWarehousesQuery();

  const { data: stats, error: statsError } = useGetWorkshopTaskStatsQuery();
  
  const [createTask] = useCreateWorkshopTaskMutation();
  const [updateTask] = useUpdateWorkshopTaskMutation();
  const [startTask] = useStartWorkshopTaskMutation();
  const [finishTask] = useFinishWorkshopTaskMutation();
  const [deleteTask] = useDeleteWorkshopTaskMutation();

  const handleOpenDialog = (task?: WorkshopTask) => {
    if (task) {
      setEditingTask(task);
      setFormData({
        scooterName: typeof task.scooter === 'string' ? '' : task.scooter.name,
        description: task.description
      });
    } else {
      setEditingTask(null);
      setFormData({
        scooterName: '',
        description: ''
      });
    }
    setDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditingTask(null);
  };

  const isFormValid = () => {
    if (editingTask) {
      return (
        formData.description !== undefined &&
        formData.description.trim() !== ''
      );
    } else {
      return (
        formData.scooterName !== undefined &&
        formData.scooterName.trim() !== '' &&
        formData.description !== undefined &&
        formData.description.trim() !== ''
      );
    }
  };

  const handleSave = async () => {
    if (!isFormValid()) {
      return;
    }

    try {
      if (editingTask) {
        await updateTask({
          id: editingTask._id,
          body: {
            description: formData.description!
          }
        }).unwrap();
      } else {
        await createTask({
          scooterName: formData.scooterName!,
          description: formData.description!
        }).unwrap();
      }
      handleCloseDialog();
    } catch (error) {
      console.error('Failed to save task:', error);
    }
  };

  const handleStart = async (taskId: string) => {
    try {
      await startTask(taskId).unwrap();
    } catch (error) {
      console.error('Failed to start task:', error);
    }
  };

  const fetchScooterNotes = async (scooterName: string) => {
    setLoadingNotes(true);
    try {
      const response = await axios.get('/scooters/note', {
        params: {
          page: 1,
          limit: 100,
          scooterName: scooterName
        }
      });
      setScooterNotes(response?.data?.data?.notes || []);
    } catch (error) {
      console.error('Failed to fetch scooter notes:', error);
      setScooterNotes([]);
    } finally {
      setLoadingNotes(false);
    }
  };

  const handleOpenFinishDialog = (task: WorkshopTask) => {
    setEditingTask(task);
    setFinishData({
      note: '',
      spareParts: [],
      photos: []
    });
    setSelectedSpareParts([]);
    setUploadedPhotos([]);
    setActiveTab(0);
    setSparePartsSearch('');
    setScooterNotes([]);
    
    const scooterName = typeof task.scooter === 'string' ? task.scooter : task.scooter.name;
    fetchScooterNotes(scooterName);
    
    setFinishDialogOpen(true);
  };

  const handleAddSparePart = () => {
    const defaultWarehouseId = warehouses && warehouses.length > 0 ? warehouses[0]._id : '';
    setSelectedSpareParts([...selectedSpareParts, { id: '', qty: 1, warehouseId: defaultWarehouseId }]);
  };

  const handleRemoveSparePart = (index: number) => {
    setSelectedSpareParts(selectedSpareParts.filter((_, i) => i !== index));
  };

  const handleSparePartChange = (index: number, field: 'id' | 'qty' | 'warehouseId', value: string | number) => {
    const newParts = [...selectedSpareParts];
    newParts[index] = { ...newParts[index], [field]: value };
    setSelectedSpareParts(newParts);
  };

  const handlePhotoUpload = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const files = event.target.files;
    if (!files) return;

    setUploading(true);
    try {
      const uploadPromises = Array.from(files).map(async (file) => {
        const formData = new FormData();
        formData.append('file', file);

        const response = await axios.post(
          `${process.env.REACT_APP_API_URL || ''}/api/files/persistent`,
          formData,
          {
            headers: {
              'Content-Type': 'multipart/form-data'
            }
          }
        );

        return response.data.path;
      });

      const uploadedPaths = await Promise.all(uploadPromises);
      setUploadedPhotos([...uploadedPhotos, ...uploadedPaths]);
    } catch (error) {
      console.error('Failed to upload photos:', error);
      alert('Помилка при завантаженні фото');
    } finally {
      setUploading(false);
    }
  };

  const handleRemovePhoto = (index: number) => {
    setUploadedPhotos(uploadedPhotos.filter((_, i) => i !== index));
  };

  const handleFinish = async () => {
    if (!editingTask || !editingTask._id) return;
    const startTime = editingTask.startAt ? moment(editingTask.startAt) : null;
    const now = moment();
    const calculatedMinutes = startTime ? now.diff(startTime, 'minutes') : 0;
    
    try {
      // Save note if provided
      if (finishData.note && finishData.note.trim()) {
        const scooterId = typeof editingTask.scooter === 'string' ? undefined : editingTask.scooter._id;
        if (scooterId) {
          dispatch(addScooterNoteAsync({
            text: finishData.note,
            scooterId: scooterId
          }));
        }
      }

      const taskId = String(editingTask._id).trim();
      const spareParts = selectedSpareParts
        .filter(sp => sp.id && sp.id.trim() && sp.warehouseId && sp.warehouseId.trim())
        .map(sp => ({ id: String(sp.id), qty: sp.qty || 0, warehouseId: String(sp.warehouseId) }));

      const result = await finishTask({
        id: taskId,
        body: {
          note: finishData.note || '',
          factMinutes: calculatedMinutes,
          spareParts: spareParts,
          photos: uploadedPhotos || []
        }
      }).unwrap();
      
      console.log('Task finished successfully:', result);
      setFinishDialogOpen(false);
      setEditingTask(null);
      setFinishData({ note: '', spareParts: [], photos: [] });
      setSelectedSpareParts([]);
      setUploadedPhotos([]);
    } catch (error: any) {
      console.error('Failed to finish task:', error);
      // Don't show alert if the error is just response parsing
      // Task might still be completed on backend even if response has issues
      if (error?.status === 500 || error?.data?.statusCode === 500) {
        console.warn('Server error but task might be completed:', error);
        // Still close the dialog as task was likely processed
        setFinishDialogOpen(false);
        setEditingTask(null);
        setFinishData({ note: '', spareParts: [], photos: [] });
        setSelectedSpareParts([]);
        setUploadedPhotos([]);
      }
    }
  };

  const handleDelete = async (id: string) => {
    if (window.confirm('Видалити задачу?')) {
      try {
        await deleteTask(id).unwrap();
      } catch (error) {
        console.error('Failed to delete task:', error);
      }
    }
  };

  const getStatusColor = (status: WorkshopTaskStatus) => {
    switch (status) {
      case WorkshopTaskStatus.PENDING:
        return 'warning';
      case WorkshopTaskStatus.PROCESSING:
        return 'error';
      case WorkshopTaskStatus.IN_CITY:
        return 'info';
      case WorkshopTaskStatus.DONE:
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status: WorkshopTaskStatus) => {
    switch (status) {
      case WorkshopTaskStatus.PENDING:
        return 'Очікує';
      case WorkshopTaskStatus.PROCESSING:
        return 'В процесі';
      case WorkshopTaskStatus.IN_CITY:
        return 'Зламаний в місті';
      case WorkshopTaskStatus.DONE:
        return 'Завершено';
      default:
        return status;
    }
  };

  const getStatusDisplay = (status: WorkshopTaskStatus) => {
    switch (status) {
      case WorkshopTaskStatus.PENDING:
        return { label: 'Очікує', color: '#FF9800', bgcolor: 'rgba(255, 152, 0, 0.12)' };
      case WorkshopTaskStatus.PROCESSING:
        return { label: 'В процесі', color: '#F44336', bgcolor: 'rgba(244, 67, 54, 0.12)' };
      case WorkshopTaskStatus.IN_CITY:
        return { label: 'Зламаний в місті', color: '#2196F3', bgcolor: 'rgba(33, 150, 243, 0.12)' };
      case WorkshopTaskStatus.DONE:
        return { label: 'Завершено', color: '#4CAF50', bgcolor: 'rgba(76, 175, 80, 0.12)' };
      default:
        return { label: status, color: '#757575', bgcolor: 'rgba(117, 117, 117, 0.12)' };
    }
  };

  const getScooterStatusDisplay = (status: ScooterStatus) => {
    let bgcolor: string;
    let color: string;
    let label: string;
    
    switch (status) {
      case ScooterStatus.AVAILABLE:
        bgcolor = 'rgba(7, 197, 11, 0.12)';
        color = '#07c50b';
        label = 'Активний для оренди';
        break;
      case ScooterStatus.CHARGING:
        bgcolor = 'rgba(116, 43, 255, 0.12)';
        color = '#742BFF';
        label = 'На заряді';
        break;
      case ScooterStatus.TRANSPORTING:
        bgcolor = 'rgba(234, 127, 92, 0.12)';
        color = '#EA7F5C';
        label = 'Транспортування';
        break;
      case ScooterStatus.MAINTENANCE:
        bgcolor = 'rgba(151, 20, 232, 0.12)';
        color = '#9714E8';
        label = 'В ремонті';
        break;
      case ScooterStatus.BROKEN_IN_CITY:
        bgcolor = 'rgba(40, 149, 234, 0.12)';
        color = '#2895EA';
        label = 'Зламаний в місті';
        break;
      case ScooterStatus.LOW_BATTERY:
        bgcolor = 'rgba(255, 87, 34, 0.12)';
        color = '#FF5722';
        label = 'Низька батарея';
        break;
      case ScooterStatus.HIDDEN:
        bgcolor = 'rgba(117, 117, 117, 0.12)';
        color = '#757575';
        label = 'Прихований';
        break;
      default:
        bgcolor = 'rgba(62, 138, 140, 0.12)';
        color = '#3E8A8C';
        label = status;
    }
    
    return { label, color, bgcolor };
  };

  const handleStatusClick = (event: React.MouseEvent<HTMLElement>, taskId: string) => {
    setStatusMenuAnchor({ element: event.currentTarget, taskId });
  };

  const handleStatusMenuClose = () => {
    setStatusMenuAnchor({ element: null, taskId: '' });
  };

  const handleStatusChange = async (newStatus: WorkshopTaskStatus) => {
    if (statusMenuAnchor.taskId) {
      try {
        handleStatusMenuClose();
        await updateTask({
          id: statusMenuAnchor.taskId,
          body: { status: newStatus }
        }).unwrap();
      } catch (error) {
        console.error('Failed to update task status:', error);
      }
    }
  };

  const handleScooterStatusClick = (event: React.MouseEvent<HTMLElement>, scooterId: string) => {
    setScooterStatusMenuAnchor({ element: event.currentTarget, scooterId });
  };

  const handleScooterStatusMenuClose = () => {
    setScooterStatusMenuAnchor({ element: null, scooterId: '' });
  };

  const handleScooterStatusChange = async (newStatus: ScooterStatus) => {
    if (scooterStatusMenuAnchor.scooterId) {
      try {
        handleScooterStatusMenuClose();
        await dispatch(
          updateScooterStatusAsync(scooterStatusMenuAnchor.scooterId, { status: newStatus }) as any
        );
      } catch (error) {
        console.error('Error updating scooter status:', error);
      }
    }
  };

  const handleStatusCardClick = (status: WorkshopTaskStatus | '') => {
    if (statusFilter === status) {
      // If clicking on active filter, clear it
      setStatusFilter('');
    } else {
      // Set new filter
      setStatusFilter(status);
    }
    setPage(1); // Reset to first page
  };

  // Show message if no city is assigned
  if (!currentCityId) {
    return (
      <Root>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Title>Задачі майстерні</Title>
        </Box>
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="400px"
          gap={2}
        >
          <LocationCity sx={{ fontSize: 80, color: 'text.secondary', opacity: 0.5 }} />
          <Typography variant="h6" color="textSecondary" textAlign="center">
            Місто не призначено
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" maxWidth={500}>
            Для перегляду задач майстерні необхідно призначити місто до вашого акаунта. 
            Зверніться до адміністратора системи для призначення міста.
          </Typography>
        </Box>
      </Root>
    );
  }

  // Check if user has access permission (403 Forbidden error)
  const hasAccessError = (tasksError as any)?.status === 403 || (statsError as any)?.status === 403;
  
  if (hasAccessError) {
    return (
      <Root>
        <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
          <Title>Задачі майстерні</Title>
        </Box>
        <Box 
          display="flex" 
          flexDirection="column"
          justifyContent="center" 
          alignItems="center" 
          minHeight="400px"
          gap={2}
        >
          <Block sx={{ fontSize: 80, color: 'error.main', opacity: 0.5 }} />
          <Typography variant="h6" color="error" textAlign="center">
            Немає доступу
          </Typography>
          <Typography variant="body2" color="textSecondary" textAlign="center" maxWidth={500}>
            У вашої ролі немає прав доступу до задач майстерні. 
            Зверніться до адміністратора системи для надання необхідних прав доступу.
          </Typography>
        </Box>
      </Root>
    );
  }

  return (
    <Root>
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Title>Задачі майстерні</Title>
        <Button
          variant="contained"
          startIcon={<Add />}
          onClick={() => handleOpenDialog()}
        >
          Додати задачу
        </Button>
      </Box>

      {stats && (
        <StatsSection>
          <Grid container spacing={2}>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                bgColor="#4ECDC4"
                isActive={statusFilter === WorkshopTaskStatus.PENDING}
                onClick={() => handleStatusCardClick(WorkshopTaskStatus.PENDING)}
              >
                <Box display="flex" alignItems="center">
                  <IconCircle bgColor="#4ECDC4">
                    <Pending />
                  </IconCircle>
                  <LabelText>Очікують</LabelText>
                </Box>
                <CountText textColor="#4ECDC4">{stats.pending}</CountText>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                bgColor="#F44336"
                isActive={statusFilter === WorkshopTaskStatus.PROCESSING}
                onClick={() => handleStatusCardClick(WorkshopTaskStatus.PROCESSING)}
              >
                <Box display="flex" alignItems="center">
                  <IconCircle bgColor="#F44336">
                    <Build />
                  </IconCircle>
                  <LabelText>В процесі</LabelText>
                </Box>
                <CountText textColor="#F44336">{stats.processing}</CountText>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                bgColor="#4CAF50"
                isActive={statusFilter === WorkshopTaskStatus.DONE}
                onClick={() => handleStatusCardClick(WorkshopTaskStatus.DONE)}
              >
                <Box display="flex" alignItems="center">
                  <IconCircle bgColor="#4CAF50">
                    <CheckCircle />
                  </IconCircle>
                  <LabelText>Завершено сьогодні</LabelText>
                </Box>
                <CountText textColor="#4CAF50">{stats.doneToday}</CountText>
              </StatCard>
            </Grid>
            <Grid item xs={12} sm={6} md={3}>
              <StatCard 
                bgColor="#2196F3"
                isActive={statusFilter === WorkshopTaskStatus.IN_CITY}
                onClick={() => handleStatusCardClick(WorkshopTaskStatus.IN_CITY)}
              >
                <Box display="flex" alignItems="center">
                  <IconCircle bgColor="#2196F3">
                    <LocationCity />
                  </IconCircle>
                  <LabelText>Зламаних в місті</LabelText>
                </Box>
                <CountText textColor="#2196F3">{stats.inCity}</CountText>
              </StatCard>
            </Grid>
          </Grid>
        </StatsSection>
      )}

      <FilterSection>
        <TextField
          label="Пошук за самокатом"
          value={scooterNameFilter}
          onChange={e => setScooterNameFilter(e.target.value)}
          size="small"
          sx={{ minWidth: 250 }}
        />
        <FormControl size="small" sx={{ minWidth: 200 }}>
          <InputLabel>Статус</InputLabel>
          <Select
            value={statusFilter}
            onChange={e => setStatusFilter(e.target.value as WorkshopTaskStatus | '')}
            label="Статус"
          >
            <MenuItem value="">Всі</MenuItem>
            <MenuItem value="PENDING">Очікують</MenuItem>
            <MenuItem value="PROCESSING">В процесі</MenuItem>
            <MenuItem value="IN_CITY">Зламаний в місті</MenuItem>
            <MenuItem value="DONE">Завершено</MenuItem>
          </Select>
        </FormControl>
      </FilterSection>

      <TableContainer component={Paper} sx={{ borderRadius: '12px', overflow: 'hidden' }}>
        {isLoading ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <CircularProgress />
          </Box>
        ) : !tasksData?.data || tasksData.data.length === 0 ? (
          <Box display="flex" justifyContent="center" alignItems="center" minHeight="200px">
            <Typography variant="body1" color="textSecondary">
              Задачі не знайдено
            </Typography>
          </Box>
        ) : (
          <Table>
            <TableHead>
              <TableRow>
                <TableHeaderCell>Самокат</TableHeaderCell>
                <TableHeaderCell>Опис</TableHeaderCell>
                <TableHeaderCell>Статус ремонту</TableHeaderCell>
                <TableHeaderCell>Створено</TableHeaderCell>
                <TableHeaderCell>Дії</TableHeaderCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {tasksData.data.map(task => {
                const scooterData = typeof task.scooter === 'string' ? null : task.scooter;
                
                return (
                <StyledTableRow key={task._id}>
                  <StyledTableCell>
                    {typeof task.scooter === 'string' ? task.scooter : task.scooter.name}
                  </StyledTableCell>
                  <StyledTableCell>{task.description}</StyledTableCell>
                  <StyledTableCell>
                    <Chip
                      label={getStatusLabel(task.status)}
                      color={getStatusColor(task.status)}
                      size="small"
                    />
                  </StyledTableCell>
                  <StyledTableCell>
                    {moment(task.createdAt).format('DD.MM.YYYY HH:mm')}
                  </StyledTableCell>
                  <StyledTableCell>
                    <Box display="flex" gap={1} justifyContent="flex-end">
                      {scooterData && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={(e) => handleScooterStatusClick(e, scooterData._id)}
                        >
                          Статус
                        </Button>
                      )}
                      {task.status === WorkshopTaskStatus.PENDING && (
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleStart(task._id)}
                        >
                          Почати
                        </Button>
                      )}
                      {task.status === WorkshopTaskStatus.PROCESSING && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenFinishDialog(task)}
                        >
                          Завершити
                        </Button>
                      )}
                      {task.status === WorkshopTaskStatus.PENDING && (
                        <Button
                          size="small"
                          variant="outlined"
                          onClick={() => handleOpenDialog(task)}
                        >
                          Редагувати
                        </Button>
                      )}
                      <Button
                        size="small"
                        variant="outlined"
                        color="error"
                        onClick={() => handleDelete(task._id)}
                      >
                        Видалити
                      </Button>
                    </Box>
                  </StyledTableCell>
                </StyledTableRow>
              );
              })}
            </TableBody>
          </Table>
        )}
      </TableContainer>

      {tasksData && tasksData.pagination.totalPages > 1 && (
        <PaginationWrapper>
          <MuiPagination
            count={tasksData.pagination.totalPages}
            page={page}
            onChange={(e, newPage) => setPage(newPage)}
            color="primary"
          />
        </PaginationWrapper>
      )}

      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {editingTask ? 'Редагувати задачу' : 'Додати задачу'}
        </DialogTitle>
        <DialogContent>
          <Box display="flex" flexDirection="column" gap={2} mt={1}>
            <TextField
              label="Назва самоката"
              value={formData.scooterName}
              onChange={e => setFormData({ ...formData, scooterName: e.target.value })}
              fullWidth
              required
              disabled={!!editingTask}
            />
            <TextField
              label="Опис"
              value={formData.description}
              onChange={e => setFormData({ ...formData, description: e.target.value })}
              fullWidth
              required
              multiline
              rows={3}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Скасувати</Button>
          <Button onClick={handleSave} variant="contained" disabled={!isFormValid()}>
            Зберегти
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={finishDialogOpen} onClose={() => setFinishDialogOpen(false)} maxWidth="md" fullWidth>
        <DialogTitle>
          <Box display="flex" justifyContent="space-between" alignItems="center">
            <span>Завершення ремонту - {editingTask ? (typeof editingTask.scooter === 'string' ? editingTask.scooter : editingTask.scooter.name) : ''}</span>
            <IconButton size="small" onClick={() => setFinishDialogOpen(false)}>
              <Close />
            </IconButton>
          </Box>
        </DialogTitle>
        <Divider />
        <DialogContent sx={{ p: 0 }}>
          <Tabs value={activeTab} onChange={(e, newValue) => setActiveTab(newValue)} sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tab label="Запчастини" />
            <Tab label="Фото" />
            <Tab label="Примітки" />
          </Tabs>

          <Box sx={{ p: 3 }}>
            {activeTab === 0 && (
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                      <Typography variant="h6">Доступні запчастини</Typography>
                      <TextField
                        size="small"
                        placeholder="Пошук..."
                        value={sparePartsSearch}
                        onChange={(e) => setSparePartsSearch(e.target.value)}
                        sx={{ width: 200 }}
                      />
                    </Box>
                    <Box sx={{ maxHeight: 300, overflowY: 'auto' }}>
                      {sparePartsData?.data
                        .filter((sparePart) => 
                          sparePart.name.toLowerCase().includes(sparePartsSearch.toLowerCase())
                        )
                        .map((sparePart) => {
                        const alreadyAdded = selectedSpareParts.some(sp => sp.id === sparePart._id);
                        return (
                          <Paper key={sparePart._id} sx={{ p: 1.5, mb: 1, display: 'flex', justifyContent: 'space-between', alignItems: 'center', backgroundColor: 'rgba(78, 205, 196, 0.08)' }}>
                            <Box>
                              <Typography variant="body1">{sparePart.name}</Typography>
                              <Typography variant="caption" color="textSecondary">
                                На складі: {sparePart.stockCurrentCity?.available || 0} шт.
                              </Typography>
                            </Box>
                            <IconButton 
                              size="small" 
                              onClick={() => {
                                if (!alreadyAdded) {
                                  const defaultWarehouseId = warehouses && warehouses.length > 0 ? warehouses[0]._id : '';
                                  setSelectedSpareParts([...selectedSpareParts, { id: sparePart._id, qty: 1, warehouseId: defaultWarehouseId }]);
                                }
                              }}
                              disabled={alreadyAdded}
                            >
                              <Add />
                            </IconButton>
                          </Paper>
                        );
                      })}
                    </Box>
                  </Box>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography variant="h6" mb={2}>Використані запчастини</Typography>
                    {selectedSpareParts.length === 0 ? (
                      <Typography variant="body2" color="textSecondary">Запчастини не вибрано</Typography>
                    ) : (
                      <Box>
                        {selectedSpareParts.map((sparePart, index) => {
                          const sparePartData = sparePartsData?.data.find(sp => sp._id === sparePart.id);
                          return (
                            <Paper key={index} sx={{ p: 1.5, mb: 1, backgroundColor: 'rgba(78, 205, 196, 0.08)' }}>
                              <Box display="flex" gap={1} alignItems="center">
                                <Box flex={1}>
                                  <Typography variant="body1">{sparePartData?.name || 'Невідома запчастина'}</Typography>
                                  <Box display="flex" gap={1} alignItems="center" mt={0.5} flexWrap="wrap">
                                    <TextField
                                      label="Кількість"
                                      type="number"
                                      value={sparePart.qty}
                                      onChange={e => handleSparePartChange(index, 'qty', parseInt(e.target.value) || 1)}
                                      size="small"
                                      sx={{ width: 100 }}
                                    />
                                    <FormControl size="small" sx={{ minWidth: 150 }}>
                                      <InputLabel>Склад</InputLabel>
                                      <Select
                                        value={sparePart.warehouseId}
                                        label="Склад"
                                        onChange={e => handleSparePartChange(index, 'warehouseId', e.target.value)}
                                      >
                                        {warehouses?.map(warehouse => (
                                          <MenuItem key={warehouse._id} value={warehouse._id}>
                                            {warehouse.name}
                                          </MenuItem>
                                        ))}
                                      </Select>
                                    </FormControl>
                                  </Box>
                                </Box>
                                <IconButton onClick={() => handleRemoveSparePart(index)} size="small">
                                  <Close />
                                </IconButton>
                              </Box>
                            </Paper>
                          );
                        })}
                      </Box>
                    )}
                  </Box>
                </Grid>
              </Grid>
            )}

            {activeTab === 1 && (
              <Box>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
                  <Typography variant="h6">Фото</Typography>
                  <Button
                    variant="outlined"
                    component="label"
                    disabled={uploading}
                    size="small"
                  >
                    Додати фото
                    <input
                      type="file"
                      hidden
                      multiple
                      accept="image/*"
                      onChange={handlePhotoUpload}
                    />
                  </Button>
                </Box>
                {uploadedPhotos.length === 0 ? (
                  <Typography variant="body2" color="textSecondary">Фото не додано</Typography>
                ) : (
                  <Grid container spacing={2}>
                    {uploadedPhotos.map((photo, index) => (
                      <Grid item xs={6} sm={4} key={index}>
                        <Paper sx={{ p: 1, position: 'relative' }}>
                          <IconButton 
                            size="small" 
                            onClick={() => handleRemovePhoto(index)}
                            sx={{ position: 'absolute', top: 4, right: 4, backgroundColor: 'rgba(255,255,255,0.8)' }}
                          >
                            <Close fontSize="small" />
                          </IconButton>
                          <Typography variant="caption" noWrap>{photo.split('/').pop()}</Typography>
                        </Paper>
                      </Grid>
                    ))}
                  </Grid>
                )}
              </Box>
            )}

            {activeTab === 2 && (
              <Box>
                <Box>
                  <Typography variant="h6" mb={2}>Нова примітка</Typography>
                  <TextField
                    label="Примітка"
                    value={finishData.note}
                    onChange={e => setFinishData({ ...finishData, note: e.target.value })}
                    fullWidth
                    multiline
                    rows={4}
                    placeholder="Додайте примітки до ремонту..."
                    sx={{ mb: 3 }}
                  />
                </Box>

                <Box>
                  <Typography variant="h6" mb={2}>Історія примітак</Typography>
                  {loadingNotes ? (
                    <Box display="flex" justifyContent="center" p={2}>
                      <CircularProgress size={24} />
                    </Box>
                  ) : scooterNotes.length === 0 ? (
                    <Typography variant="body2" color="textSecondary">
                      Немає попередніх примітак для цього самоката
                    </Typography>
                  ) : (
                    <Box sx={{ maxHeight: 300, overflowY: 'auto', border: '1px solid', borderColor: 'divider', borderRadius: 1, p: 0 }}>
                      {scooterNotes.map((note, index) => (
                        <Paper
                          key={note._id || index}
                          elevation={0}
                          sx={{
                            p: 1.5,
                            borderBottom: index < scooterNotes.length - 1 ? '1px solid' : 'none',
                            borderBottomColor: 'divider',
                            backgroundColor: 'transparent',
                            '&:hover': {
                              backgroundColor: 'rgba(0, 0, 0, 0.02)'
                            }
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="flex-start" mb={0.5}>
                            <Typography variant="body2" fontWeight={600}>
                              {note.adminLogin}
                            </Typography>
                            <Typography variant="caption" color="textSecondary">
                              {moment(note.createdAt).format('DD.MM.YYYY HH:mm')}
                            </Typography>
                          </Box>
                          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {note.text}
                          </Typography>
                        </Paper>
                      ))}
                    </Box>
                  )}
                </Box>
              </Box>
            )}
          </Box>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ p: 2, display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="body2">
            Час роботи: {editingTask?.startAt ? moment().diff(moment(editingTask.startAt), 'minutes') : 0} хв
          </Typography>
          <Box display="flex" gap={1}>
            <Button onClick={() => setFinishDialogOpen(false)}>Скасувати</Button>
            <Button onClick={handleFinish} variant="contained" sx={{ backgroundColor: '#FDD835', color: 'black', '&:hover': { backgroundColor: '#FBC02D' } }}>
              Завершити ремонт
            </Button>
          </Box>
        </DialogActions>
      </Dialog>

      <Menu
        anchorEl={statusMenuAnchor.element}
        open={Boolean(statusMenuAnchor.element)}
        onClose={handleStatusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        {Object.values(WorkshopTaskStatus).map((status) => {
          const display = getStatusDisplay(status);
          return (
            <MenuItem 
              key={status} 
              onClick={() => handleStatusChange(status)}
              sx={{ 
                minWidth: 200,
                gap: 1.5,
                py: 1.5,
                px: 2
              }}
            >
              <Chip 
                label={display.label}
                size="small"
                sx={{ 
                  backgroundColor: display.bgcolor,
                  color: display.color,
                  fontWeight: 600,
                  pointerEvents: 'none'
                }} 
              />
            </MenuItem>
          );
        })}
      </Menu>

      <Menu
        anchorEl={scooterStatusMenuAnchor.element}
        open={Boolean(scooterStatusMenuAnchor.element)}
        onClose={handleScooterStatusMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'left',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'left',
        }}
        PaperProps={{
          sx: {
            mt: 1,
            borderRadius: 2,
            boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
          }
        }}
      >
        {Object.values(ScooterStatus).map((status) => {
          const display = getScooterStatusDisplay(status);
          return (
            <MenuItem 
              key={status} 
              onClick={() => handleScooterStatusChange(status)}
              sx={{ 
                minWidth: 220,
                gap: 1.5,
                py: 1.5,
                px: 2
              }}
            >
              <Chip 
                label={display.label}
                size="small"
                sx={{ 
                  backgroundColor: display.bgcolor,
                  color: display.color,
                  fontWeight: 600,
                  pointerEvents: 'none'
                }} 
              />
            </MenuItem>
          );
        })}
      </Menu>
    </Root>
  );
};

export default WorkshopTasksPage;
